"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { postQuestion } from "@/api/auth";
import { ErrorNotif } from "@/components/errorNotif/error";
import { useState } from "react";
import SimpleTextField from "@/components/simple-text-field";
import PersianLetterInput from "@/components/persian-letter-input";
import NumberInput from "@/components/number-input";
import { Button } from "@/components/button";
import SuccessModal from "@/components/success-modal";
import {
  ticketSchema,
  TicketFormValues,
} from "@/components/schema/contact-us";

export function QuestionForm() {
  const [errorMessage, setErrorMessage] = useState("");
  const [successModal, setSuccessModal] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TicketFormValues>({
    resolver: zodResolver(ticketSchema),
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile: "",
      email: "",
      subject: "",
      body: "",
    },
  });
  const onSubmit: SubmitHandler<TicketFormValues> = async (data) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        formData.append(k, v ?? "");
      });
      await postQuestion(formData);
      setSuccessModal(true);
      reset();
      setErrorMessage("")
    } catch (e: any) {
      console.error(e?.response?.data?.message || "خطا در ارسال اطلاعات");
      setErrorMessage(e?.response?.data?.message || "خطایی رخ داد");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-6 p-5"
    >
      <p id="add-question-form" className="font-bold text-right text-2xl text-white mb-2">
        پیام خود را برای ما ارسال کنید.
      </p>

      <PersianLetterInput
        label="نام"
        placeholder="مثال: علی"
        {...register("first_name")}
        errors={errors.first_name}
        maxLength={50}
        requiredMark
      />

      <PersianLetterInput
        label="نام خانوادگی"
        placeholder="مثال: رضایی"
        {...register("last_name")}
        maxLength={50}
        errors={errors.last_name}
        requiredMark
      />

      <NumberInput
        label="شماره تلفن همراه"
        placeholder="مثال: 09121234567"
        {...register("mobile")}
        maxLength={11}
        minLength={11}
        errors={errors.mobile}
        requiredMark
      />

      <SimpleTextField
        type="email"
        label="ایمیل"
        labelClassName="text-white"
        placeholder="example@mail.com"
        {...register("email")}
        errors={errors.email}
      />

      <SimpleTextField
        label="موضوع"
        placeholder="مثال: مشکل در ورود"
        labelClassName="text-white"
        {...register("subject")}
        errors={errors.subject}
        maxLength={200}
        requiredMark
      />

      <SimpleTextField
        label="توضیحات"
        placeholder="توضیحات را وارد کنید"
        labelClassName="text-white"
        {...register("body")}
        errors={errors.body}
        maxLength={2000}
        as="textarea"
        rows={5}
        requiredMark
      />

      {errorMessage && (
        <div className=" my-2">
          <ErrorNotif description={errorMessage} />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
        <Button
          variant="bare"
          type="submit"
          text={isSubmitting ? "در حال ارسال..." : " ارسال پیام"}
          disabled={isSubmitting}
          className="h-11 bg-Accent hover:bg-Accent-700 text-gray-600 font-medium rounded-3xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        />
      </div>

      <SuccessModal
        description="پیام شما با موفقیت ثبت شد."
        onClose={() => setSuccessModal(false)}
        open={successModal}
      />

    </form>
  );
}
